



import {
  type DialpadUsersResponse,
} from "../../lib/api";
import {
  fmtDateTime,
} from "../../lib/format";
import {
  CollapsibleSection,
} from "./shared";

export function DialpadUsersSection({
  usersQ,
  onSync,
  syncing,
  syncError,
  stuck = false,
}: {
  usersQ: { data?: DialpadUsersResponse; isLoading: boolean };
  onSync: () => void;
  syncing: boolean;
  syncError: string | null;
  // The sync has been queued/running far longer than normal — the worker is
  // likely down. Surfaced as a warning so "Queuing…" isn't a silent dead end.
  stuck?: boolean;
}) {
  const data = usersQ.data;
  const brands = data ? Object.entries(data.by_brand) : [];
  return (
    <CollapsibleSection
      title="Dialpad Users"
      ariaLabel="Dialpad users"
      autoExpand={!!syncError || stuck}
      subtitle={
        <>
          {data ? `${data.total} total` : "—"}
          {data?.last_synced_at && (
            <> · last synced {fmtDateTime(data.last_synced_at)}</>
          )}
        </>
      }
      headerActions={
        <>
          {syncError && <span className="text-xs text-destructive">{syncError}</span>}
          {!syncError && stuck && (
            <span className="text-xs text-muted-foreground">
              Taking longer than usual — the sync worker may be unavailable.
            </span>
          )}
          <button
            type="button"
            onClick={onSync}
            disabled={syncing}
            className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            {syncing ? "Queuing…" : "Sync users"}
          </button>
        </>
      }
    >
      {brands.length > 0 && (
        <div className="px-4 py-2 flex flex-wrap gap-2 border-b border-border">
          {brands.map(([brand, count]) => (
            <span
              key={brand}
              className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground text-xs px-2 py-0.5"
            >
              <span className="font-medium">{brand}</span>
              <span className="text-muted-foreground">{count}</span>
            </span>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-72">
        <table className="w-full text-sm">
          <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Brand</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">License</th>
              <th className="text-left px-3 py-2">Synced</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]">
                <td className="px-3 py-2">{u.display_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{u.email || "—"}</td>
                <td className="px-3 py-2 text-xs">{u.brand || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{u.state || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{u.license || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {fmtDateTime(u.synced_at)}
                </td>
              </tr>
            ))}
            {usersQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : (
              (data?.rows.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    No users synced yet. Click "Sync users" to fetch from Dialpad.
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}

